import { emailRegex } from '@frontend/components/basic-forms';
import { useLocale } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import type { Dispatch, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY, findUsersByEmail, getUserEmailLookupField } from '../../utils/open-drop-off-user-lookup';
import { useScreen } from '../useScreen';

interface ViewProps {
    domain: string | null;
    emailInput: string;
    setEmailInput: Dispatch<string>;
    email: string;
    isLoadingSettings: boolean;
    isSubmitting: boolean;
    submitDisabled: boolean;
    error: ReactNode | null;
    resetError: () => void;
    onSubmit: () => Promise<void>;
    onBack: () => void;
}

const normalizeDomain = (value: string) => value.trim().replace(/^@+/, '');

const resolveEmail = (emailInput: string, domain: string | null) => {
    const trimmedEmailInput = emailInput.trim();
    if (!domain) return trimmedEmailInput;
    return `${trimmedEmailInput}@${domain}`;
};

const hasValidEmailInput = (emailInput: string, email: string, domain: string | null) => {
    if (emailInput.trim().length === 0) return false;
    if (domain && emailInput.includes('@')) return false;
    return emailRegex.test(email);
};

const buildCreateUserPayload = (email: string, uniqueIdentifierIsEmail: boolean) => ({
    email,
    unique_identifier: uniqueIdentifierIsEmail ? email : crypto.randomUUID()
});

const hasTrackingNumber = (...values: unknown[]) => values.some((value) => typeof value === 'string' && value.trim().length > 0);

const useCreateReceiver = (): ViewProps => {
    const intl = useIntl();
    const { locale } = useLocale();
    const { store, wizardId, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const { store: accountStore } = useAccountRepository();
    const { syncTransaction } = useWizardTransaction({
        wizardId,
        store,
        autoCreate: false,
        autoFetch: true
    });

    const [domain, setDomain] = useState<string | null>(null);
    const [emailInput, setEmailInputState] = useState(() => (typeof store?.data.createReceiverEmail === 'string' ? store.data.createReceiverEmail : ''));
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<ReactNode | null>(null);

    const accountId = store?.wizard.account_id;
    const lookupField = accountId ? getUserEmailLookupField(accountStore.availableAccountsInfo, accountId) : 'email';
    const email = useMemo(() => resolveEmail(emailInput, domain), [domain, emailInput]);
    const emailValid = hasValidEmailInput(emailInput, email, domain);

    const setEmailInput: Dispatch<string> = (value) => {
        setEmailInputState(domain ? value.split('@')[0] : value);
    };

    useEffect(() => {
        if (!domain) return;

        setEmailInputState((current) => {
            const trimmed = current.trim();
            const suffix = `@${domain.toLowerCase()}`;
            return trimmed.toLowerCase().endsWith(suffix) ? trimmed.slice(0, trimmed.length - suffix.length) : current;
        });
    }, [domain]);

    useEffect(() => {
        if (!accountId) {
            setIsLoadingSettings(false);
            return;
        }

        setIsLoadingSettings(true);
        SettingClient.fetchSettings({
            account_id: accountId,
            entity_id: accountId,
            key: SettingKey.DOMAIN
        })
            .then((response) => {
                const domainSetting = response.results.find((setting) => setting.key === SettingKey.DOMAIN)?.value;
                setDomain(typeof domainSetting === 'string' && normalizeDomain(domainSetting).length > 0 ? normalizeDomain(domainSetting) : null);
            })
            .catch((e) => {
                Logger.error('Failed to fetch create receiver domain setting', e);
                setDomain(null);
            })
            .finally(() => setIsLoadingSettings(false));
    }, [accountId]);

    const findExistingReceiverUsers = async (): Promise<IUser[] | null> => {
        if (!accountId) return null;

        const existingUsers = await findUsersByEmail(accountId, email, lookupField);
        if (existingUsers.length > 1) {
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.create-receiver.duplicate-email-error',
                    description: 'Error shown when the receiver email belongs to multiple existing users',
                    defaultMessage: 'That email address matches multiple users. Please ask support to select the right receiver.'
                })
            );
            return null;
        }

        return existingUsers;
    };

    const createReceiver = () => {
        if (!accountId) throw new Error('Cannot create receiver: missing account ID');
        return UserClient.postUser(accountId, {
            ...buildCreateUserPayload(email, lookupField === 'unique_identifier'),
            language: locale
        });
    };

    const onSubmit = async () => {
        if (!store || !accountId || !emailValid || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const existingUsers = await findExistingReceiverUsers();
            if (!existingUsers) return;

            const transaction = await syncTransaction();
            const receiverUser = existingUsers[0] ?? (await createReceiver());
            const existingReceivers = await TransactionUserClient.fetchTransactionUsers({
                transaction_id: transaction.id,
                type: TransactionUserType.RECEIVER
            });
            const existingReceiver = existingReceivers.results.find((receiver) => receiver.user_id === receiverUser.id);
            const receiver: TransactionUser =
                existingReceiver ??
                (await TransactionUserClient.postTransactionUser(accountId, transaction.id, {
                    user_id: receiverUser.id,
                    type: TransactionUserType.RECEIVER
                }));

            updateWizardData({
                receivers: [receiver],
                transaction,
                createReceiverEmail: undefined,
                [OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY]: undefined
            });

            const nextStepTrigger = hasTrackingNumber(store.data.trackingNumber, transaction.tracking_number)
                ? ScreenTrigger.SUBMIT_TRUE
                : ScreenTrigger.SUBMIT;
            const nextStep = store.steps.find((step) => step.screen === ScreenType.CREATE_RECEIVER && step.trigger === nextStepTrigger);
            onContinue(nextStep);
        } catch (e) {
            Logger.error('Failed to create receiver', e);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.create-receiver.create-error',
                    description: 'Error shown when creating a receiver and linking it to the transaction fails',
                    defaultMessage: 'We could not create the receiver. Please try again.'
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        domain,
        emailInput,
        setEmailInput,
        email,
        isLoadingSettings,
        isSubmitting,
        submitDisabled: isLoadingSettings || isSubmitting || !accountId || !emailValid,
        error,
        resetError: () => setError(null),
        onSubmit,
        onBack
    };
};

export default useCreateReceiver;
